import { useEffect, useRef, useState } from 'react';
import QuestionIcon from '../../assets/images/question.svg';
import LanguageIconGreen from '../../assets/images/LanguageIconGreen.svg';
import DocLinkIconGreen from '../../assets/images/icon/icon-docLinkIconGreen.svg';
import PortalIcon from '../../assets/images/portal-icon.svg';
import HamburgerIcon from '../../assets/images/icon/HamburgerIcon.svg';
import brushwingLogo from '../../assets/images/cx_brushwing_logo.originalimage.png';
import closeButton from '../../assets/images/icon/icon-cross.svg';
import { Navbar } from 'react-bootstrap';
import { Nav } from 'react-bootstrap';
import { useScroll, useMotionValueEvent, AnimatePresence, motion } from 'framer-motion';
import { scrollToTop } from '../../helpers/scroll';
import { buildPathForLang } from '../../helpers/languageRouting';
import { useFeatureToggles } from '../../contexts/FeatureTogglesContext';
import { NavHashLink as Link } from 'react-router-hash-link';
import { useTranslation } from 'react-i18next';
import { getHeaderTranslations, useNavigationTranslationsQuery } from '../../services/NavigationItemServices';
import { useHomeTranslationsQuery } from '../../services/HomePageServices';
import { triggerEvent } from '../../helpers/cxDataLayer';
import { conversion } from '../../helpers/cxDataLayer';
import { useNavigate, useLocation } from 'react-router-dom';
import { MobileLanguageSelector } from '../LanguageSelector/MobileLanguageSelector';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';


interface ResponsiveNavbarProps {
  pathName: string;
}

interface NavbarTopRibbonProps {
  openSideMenuHandler: () => void;
}

type HeaderTranslationsData = Awaited<ReturnType<typeof getHeaderTranslations>>;

interface SideMenuProps {
  showMenu: boolean;
  closeSideMenuHandler: () => void;
  headerTranslations: HeaderTranslationsData;
}

interface SideLinksProps {
  closeSideMenuHandler: () => void;
  headerTranslations: HeaderTranslationsData;
}

const getStableNavKey = (item: unknown, index: number) => {
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    const candidate = obj.id ?? obj.code ?? obj.slug ?? obj.path ?? obj.key ?? obj.title ?? obj.label;
    if (typeof candidate === 'string' || typeof candidate === 'number') {
      return String(candidate);
    }
  }
  return `nav-${index}`;
};

const LANGUAGE_OPTIONS = [
  {
    code: 'en_HK',
    label: 'English'
  },
  {
    code: 'zh_HK',
    label: '繁體中文'
  },
  {
    code: 'sc_CN',
    label: '简体中文'
  }
] as const;

const getLanguageLabel = (lang: string) =>
  LANGUAGE_OPTIONS.find((item) => item.code === lang)?.label ?? LANGUAGE_OPTIONS[0].label;

const hasCmsValue = (value: string, key: string) => value.trim() !== '' && value !== key;

const buildLanguageUrl = (lang: string, pathname: string, search: string, hash: string) =>
  `${buildPathForLang(lang, pathname)}${search}${hash}`;

const changeLanguageAndNavigate = async (
  lang: string,
  changeLanguage: (nextLang: string) => Promise<unknown> | unknown,
  navigate: (to: string) => void,
  pathname: string,
  search: string,
  hash: string,
  onDone?: () => void
) => {
  await Promise.resolve(changeLanguage(lang));
  navigate(buildLanguageUrl(lang, pathname, search, hash));
  onDone?.();
};

const scrollWithOffset = (el: HTMLElement) => {
  const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
  const yOffset = -106; // Sorry to hard code this
  window.scrollTo({
    top: yCoordinate + yOffset,
    behavior: 'smooth'
  });
};

function ResponsiveNavbar({ pathName }: ResponsiveNavbarProps) {
  const [isScrollDown, setIsScrollDown] = useState(false);
  const [isOnTop, setIsOnTop] = useState(true);
  const [isAnimatedSticky, setIsAnimatedSticky] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const { scrollY } = useScroll();
  useHomeTranslationsQuery();
  const navigationQuery = useNavigationTranslationsQuery();
  const headerTranslations = navigationQuery.data ? getHeaderTranslations(navigationQuery.data) : [];

  useMotionValueEvent(scrollY, 'change', () => {
    // Toggle the stickyness by scroll direction
    // Magic number for the height of the navbar
    const isSmallScreen = window.innerWidth < 1024;
    const navBarHeight = isSmallScreen ? 46 : 106;

    if (scrollY.get() > navBarHeight) {
      setIsOnTop(false);

      // Stop transition at the moment that just scroll down from the top
      if (scrollY.getPrevious() <= navBarHeight) {
        setIsAnimatedSticky(false);
      } else {
        setIsAnimatedSticky(true);
      }
    } else {
      setIsOnTop(true);
      setIsAnimatedSticky(false);
    }

    if (scrollY.get() < scrollY.getPrevious()) {
      setIsScrollDown(false);
    } else if (
      scrollY.get() > navBarHeight &&
      scrollY.get() > scrollY.getPrevious()
    ) {
      setIsScrollDown(true);
    }
  });

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = showMenu ? 'hidden' : '';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showMenu]);

  const openSideMenuHandler = () => {
    setShowMenu(true);
  };

  const closeSideMenuHandler = () => {
    setShowMenu(false);
  };

  return (
    <>
      <nav
        className={`navbar-wrapper${isOnTop ? '' : ' sticky'}${isScrollDown ? '' : ' sticky-show'
        }${isAnimatedSticky ? ' sticky-transition' : ''}`}
        id="responsive-navbar"
      >
        <NavbarTopRibbon openSideMenuHandler={openSideMenuHandler} />
        <NavbarPrimary headerTranslations={headerTranslations} pathName={pathName} />
        <SideMenu
          showMenu={showMenu}
          closeSideMenuHandler={closeSideMenuHandler}
          headerTranslations={headerTranslations}
        />

        <div
          className={`dark-overlay ${showMenu ? 'showMenu' : 'hideMenu'}`}
          onClick={closeSideMenuHandler}
        ></div>
      </nav>
      <div className={`header-placeholder ${isOnTop ? '' : 'show'}`}></div>
    </>
  );
}

function NavbarTopRibbon({ openSideMenuHandler }: NavbarTopRibbonProps) {
  const { t, i18n } = useTranslation('header');
  const { portal } = useFeatureToggles();
  const location = useLocation();
  const navigate = useNavigate();
  const languageRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const cathayLinkText = t('cathay_link_text', { defaultValue: 'Cathay Pacific' });
  const cathayLinkHref = t('cathay_link_link');
  const cathayLinkAltText = t('cathay_link_icon_alt_text', { defaultValue: 'Cathay Pacific link' });
  const portalText = t('portal_text', { defaultValue: 'Portal' });
  const portalLink = t('portal_link');
  const supportText = t('support_text', { defaultValue: 'Support' });
  const supportLink = t('support_link');
  const supportAltText = t('support_icon_alt_text', { defaultValue: 'Support icon' });
  const hasCathayLink = hasCmsValue(cathayLinkHref, 'cathay_link_link');
  const hasPortalLink = hasCmsValue(portalLink, 'portal_link');
  const hasSupportLink = hasCmsValue(supportLink, 'support_link');

  const changeLanguage = async (lang: string) => {
    await changeLanguageAndNavigate(
      lang,
      (nextLang) => i18n.changeLanguage(nextLang),
      navigate,
      location.pathname,
      location.search,
      location.hash,
      () => setDropdownOpen(false)
    );
  };

  useEffect(() => {
    const clickOutsideHandler = (event: MouseEvent) => {
      if (languageRef.current && event.target && !languageRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    const scrollHandler = () => {
      setDropdownOpen(false);
    };

    document.addEventListener('mousedown', clickOutsideHandler);
    document.addEventListener('scroll', scrollHandler);

    return () => {
      document.removeEventListener('mousedown', clickOutsideHandler);
      document.removeEventListener('scroll', scrollHandler);
    };
  }, []);

  return (
    <Navbar className="navbar-top-ribbon">
      <div className="navbar-top-ribbon__container">
        <div className="navbar-top-ribbon__out-left-group">
          <Nav.Link id="cathay-link" href={hasCathayLink ? cathayLinkHref : undefined} className="navbar-top-ribbon__button">
            {cathayLinkText}
            <img className="navbar-top-ribbon__cathay-link__icon" src={DocLinkIconGreen} alt={cathayLinkAltText}></img>
          </Nav.Link>
        </div>
        <div className="navbar-top-ribbon__left-group">
          <button
            type="button"
            className="navbar-top-ribbon__burger-menu-button"
            onClick={openSideMenuHandler}
            aria-label={t('open_menu', { defaultValue: 'Open menu' })}
            aria-expanded={false}
          >
            <img src={HamburgerIcon} alt="" aria-hidden="true" />
          </button>
        </div>

        <Link
          className="navbar-top-ribbon__homelink"
          to={'/' + i18n.language}
          rel="nofollow noopener noreferrer"
          onClick={scrollToTop}
        >
          <div className="navbar-top-ribbon__homelink__icon-wrapper">
            <img
              className="navbar-top-ribbon__homelink__icon"
              src={brushwingLogo}
              alt="Brushwing"
              width="23.19"
              height="28"
            />
            <span className="visually-hidden">brushwing</span>
          </div>
        </Link>
        <div className="navbar-top-ribbon__right-group">
          <div
            className="navbar-top-ribbon__language-dropdown"
            ref={languageRef}
          >
            <button
              type="button"
              className="navbar-top-ribbon__button"
              aria-haspopup="menu"
              aria-expanded={isDropdownOpen}
              onClick={() => {
                setDropdownOpen(!isDropdownOpen);
              }}
            >
              <img className="navbar-top-ribbon__language__icon" src={LanguageIconGreen} alt="Language Icon"></img>
              {getLanguageLabel(i18n.language)}
            </button>
            {
              isDropdownOpen && <div className='navbar-top-ribbon__right-group__dropdown' role="menu">
                {LANGUAGE_OPTIONS.map(({ code, label }) => {
                  return (
                    <button
                      key={code}
                      type="button"
                      role="menuitemradio"
                      aria-checked={i18n.language === code}
                      onClick={() => {
                        void changeLanguage(code);
                      }}
                      className={i18n.language === code ? 'navbar-top-ribbon__right-group__selected' : 'navbar-top-ribbon__right-group__unselected'}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            }

          </div>
          {/* The code is for the thin line to separate the two buttons */}
          <div style={{
            width: '1px',
            height: '20px',
            backgroundColor: "#BCBEC0",
            marginLeft: '12px'
          }}>
          </div>

          {portal ? (
            <Nav.Link
              className="navbar-top-ribbon__button"
              href={hasPortalLink ? portalLink : undefined}
            >
              <img className="navbar-top-ribbon__portal__icon" src={PortalIcon} alt="Portal Icon" />
              {portalText}
              <div className="navbar-top-ribbon__separator">|</div>
            </Nav.Link>
          ) : null}
          <Nav.Link
            className="navbar-top-ribbon__button"
            href={hasSupportLink ? supportLink : undefined}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onClick={() => {}}//() => linkEvent('SUPPORT_LINK')}
          >
            <img className="navbar-top-ribbon__faq__icon" src={QuestionIcon} alt={supportAltText}></img>
            {supportText}
          </Nav.Link>
        </div>
      </div>
    </Navbar>
  );
}

function NavbarPrimary({ headerTranslations, pathName }: { headerTranslations: HeaderTranslationsData, pathName: string }) {
  const { application } = useFeatureToggles();
  const { i18n } = useTranslation();
  const { t } = useTranslation('header');
  const { t: tDetail } = useTranslation('detail');
  const homePath = pathName || `/${i18n.language}`;
  const applicationLink = tDetail('application_link');
  const applicationButtonText = tDetail('application_button_text');
  const cathayLogoAltText = t('cathay_logo_alt_text');
  const cathayLogo = t('cathay_logo');
  const hasApplicationLink = !!applicationLink && applicationLink !== 'application_link';
  const hasApplicationButtonText = !!applicationButtonText && applicationButtonText !== 'application_button_text';
  const hasCathayLogo = hasCmsValue(cathayLogo, 'cathay_logo');

  return (
    <Navbar className="navbar-primary">
      <div className="navbar-primary__container">
        <div className="navbar-primary__left-group">
          <Link
            className="navbar-primary__homelink"
            to={`/${i18n.language}`}
            onClick={scrollToTop}
          >
            {hasCathayLogo ? (
              <img
                className="navbar-primary__homelink__icon"
                src={cathayLogo}
                alt={cathayLogoAltText !== 'cathay_logo_alt_text' ? cathayLogoAltText : 'Cathay'}
                id="statisticsSection"
              />
            ) : (
              <span className="navbar-primary__homelink__fallback" id="statisticsSection">Cathay</span>
            )}
          </Link>
          {headerTranslations.map((item, index) => {
            const link = t('navigation_link_' + index).trim(); // '#section' or '/newpage'
            const label = t('navigation_title_' + index);
            const to = link.startsWith('#') ? `${homePath}${link}` : link;

            return (
              <Link
                key={getStableNavKey(item, index)}
                className="navbar-primary__nav-link"
                to={to}
                smooth
                scroll={scrollWithOffset}
              >
                {label}
              </Link>
            );
          })}
        </div>
        <div className="navbar-primary__right-group">
          {
            application && hasApplicationLink && hasApplicationButtonText ? (
              <Nav.Link
                className="navbar-primary__nav-link"
                href={applicationLink}
                target="_blank"
                style={{ fontFamily: i18n.language === 'en_HK' ? '' : 'monospace' }}
                onClick={() => {
                  conversion();
                  triggerEvent({
                    event:
                    {
                      event_action: "CATHAY_HACKATHON_HEADER::APPLY_NOW_BTN"
                    }
                  });
                }}
              >
                {applicationButtonText}
              </Nav.Link>)
              : null
          }
        </div>
      </div>
    </Navbar>
  );
}

function SideMenu({ showMenu, closeSideMenuHandler, headerTranslations }: SideMenuProps) {
  const { i18n, t } = useTranslation('header');
  const { portal } = useFeatureToggles();
  const navigate = useNavigate();
  const location = useLocation();


  const changeLanguage = async (lang: string) => {
    await changeLanguageAndNavigate(
      lang,
      (nextLang) => i18n.changeLanguage(nextLang),
      navigate,
      location.pathname,
      location.search,
      location.hash
    );
  };


  const [isDrawerOpen, setDrawerOpen] = useState(false);

  return (
    <AnimatePresence>
      {showMenu && (
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: '0%' }}
          exit={{ x: '-100%' }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
          className="sidebar"
        >
          <div className="sidebar__header">
            <img
              className="sidebar__header__image"
              src="https://www.cathaypacific.com/content/dam/header-footer-lrp/menu_home.originalimage.jpg"
              alt="Header image"
            />
            <button
              onClick={closeSideMenuHandler}
              className="sidebar__header__close btn-close shadow-none"
            >
              <img
                src={closeButton}
                alt='close'
                className="icon"
              />
              <span className="visually-hidden">close</span>
            </button>
          </div>
          <div className="sidebar__content">
            <SideLinks
              closeSideMenuHandler={closeSideMenuHandler}
              headerTranslations={headerTranslations}
            />
          </div>
          <Nav.Link
            className="sidebar__action-row sidebar__bottom__cathay-button"
            id="cathay-link-mobile"
            href={t('cathay_link_link')}
            onClick={closeSideMenuHandler}
          >
            <img className="sidebar__bottom__cathay-icon" src={DocLinkIconGreen} alt={t('cathay_link_icon_alt_text')}></img>
            {t('cathay_link_text')}
          </Nav.Link>

          {
            portal ? (
              <Nav.Link
                className="sidebar__action-row sidebar__bottom__portal-button"
                id="portal"
                href={t('portal_link')}
                onClick={closeSideMenuHandler}
              >
                <img className="sidebar__bottom__portal-icon" src={PortalIcon} alt="Portal Icon" />
                {t('portal_text')}
              </Nav.Link>
            ) : null
          }

          <div className="sidebar__bottom">
            <div className="sidebar__action-row sidebar__bottom__language-selector-row">
              <MobileLanguageSelector triggerLanguagePicker={() => {
                setDrawerOpen(true);
              }} className="sidebar__bottom__language-selector" />
            </div>

            <Drawer open={isDrawerOpen} onClose={() => { setDrawerOpen(false); }} direction="bottom">
              <div className="language-picker">
                <p className='language-picker__select-language'>{t('select_language_text', { defaultValue: 'Select your language' })}</p>
                {LANGUAGE_OPTIONS.map(({ code, label }) => {
                  const isCurrent = code === i18n.language;

                  return (
                    <button
                      key={code}
                      type="button"
                      disabled={isCurrent}
                      className={`language-picker__option ${isCurrent ? 'disabled' : 'active'}`}
                      onClick={() => {
                        if (!isCurrent) {
                          void changeLanguage(code).then(() => {
                            setDrawerOpen(false);
                            closeSideMenuHandler();
                          });
                        }
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </Drawer>
            <Nav.Link
              className="sidebar__action-row sidebar__bottom__support-button"
              id="support"
              href={t('support_link')}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onClick={() => {}}//() => linkEvent('SUPPORT_LINK')}
            >
              <img className="sidebar__bottom__support-icon" src={QuestionIcon} alt={t('support_icon_alt_text')}></img>
              {t('support_text')}
            </Nav.Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SideLinks({ closeSideMenuHandler, headerTranslations }: SideLinksProps) {
  const { t, i18n } = useTranslation('header');
  const homePath = `/${i18n.language}`;

  return (
    <>
      {headerTranslations.map((item) => {
        const linkKey = `navigation_link_${item}`;
        const titleKey = `navigation_title_${item}`;
        const link = t(linkKey).trim();
        const to = link.startsWith('#') ? `${homePath}${link}` : link;

        return (
          <Link
            key={linkKey}
            className="sidebar__nav-link"
            to={to}
            smooth
            scroll={scrollWithOffset}
            onClick={() => {
              closeSideMenuHandler();
            }}
          >
            {t(titleKey)}
          </Link>
        );
      })}
    </>
  );
}

export default ResponsiveNavbar;
